import { getTypographyDefaultColor, RatingStars, Typography } from '@ag1/ui';
import clsx from 'clsx';
import { HeroHalfV5 as HeroHalfV5Props } from 'types/studio/studio';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { urlFor } from 'shared/utils/urlFor';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import React from 'react';
import styles from './HeroHalfV5HeadlineAndSubtitle.module.scss';

type Props = Pick<
  HeroHalfV5Props,
  | 'subtitle'
  | 'headline'
  | 'ratingTitle'
  | 'ratingValue'
  | 'ratingMobilePosition'
  | 'logo'
  | 'logoSize'
  | 'logoPosition'
  | 'textAlignment'
  | 'superHeadline'
  | 'superHeadlineVariation'
> & { theme: 'old' | 'new' | undefined; visibleOnPageLoad: boolean; isDarkMode: boolean };

const HeroHalfV5HeadlineAndSubtitle = (props: Props) => {
  const {
    subtitle,
    headline,
    ratingTitle,
    ratingValue,
    ratingMobilePosition,
    theme,
    textAlignment,
    superHeadline,
    superHeadlineVariation,
    logo,
    logoSize,
    logoPosition,
    visibleOnPageLoad,
    isDarkMode
  } = props;
  return (
    <>
      {ratingValue && ratingTitle && (
        <aside
          className={clsx(
            styles.ratingBlock,
            textAlignment === 'left' && styles.left,
            ratingMobilePosition === 'row' && styles.row
          )}>
          <RatingStars
            rating={ratingValue}
            thin
            theme={theme}
            className={clsx(styles.ratingStars, ratingMobilePosition === 'row' && styles.small)}
            childClassName={ratingMobilePosition === 'row' ? '' : styles.paddingSmall}
            inheritFontSize
          />
          <Typography
            component="div"
            variant="portalCaption"
            size="2"
            noPadding
            color={getTypographyDefaultColor(theme)}
            className={clsx(
              styles.ratingTitle,
              getThemeCSSClass(theme),
              isDarkMode && styles.isDarkMode
            )}
            weight="400">
            <CustomPortableText value={ratingTitle} />
          </Typography>
        </aside>
      )}
      {logo?.asset && logoPosition !== 'below' && (
        <img
          src={urlFor(logo).url}
          alt={logo.alt}
          className={clsx(
            styles.logo,
            logoSize && styles[logoSize],
            !ratingTitle && styles.noMargin
          )}
          loading={visibleOnPageLoad ? 'eager' : 'lazy'}
        />
      )}
      {superHeadline && (
        <Typography
          variant={superHeadlineVariation || 'subtitle'}
          size="2"
          noPadding
          color={getTypographyDefaultColor(theme)}
          className={clsx(
            styles.superHeadline,
            !ratingTitle && !logo && styles.noMargin,
            isDarkMode && styles.isDarkMode
          )}
          weight="400">
          {superHeadline}
        </Typography>
      )}
      {headline && (
        <Typography
          variant="headline"
          size="1"
          component="h1"
          noPadding
          color={getTypographyDefaultColor(theme)}
          className={clsx(
            styles.headline,
            !superHeadline && !ratingTitle && !logo && styles.noMargin,
            superHeadline && styles.underSuperHeadline,
            isDarkMode && styles.isDarkMode
          )}>
          {headline.split('{break}').map((part, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {part}
            </React.Fragment>
          ))}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="subtitle"
          size="2"
          noPadding
          color={getTypographyDefaultColor(theme)}
          className={clsx(styles.subtitle, isDarkMode && styles.isDarkMode)}
          weight="700">
          {subtitle}
        </Typography>
      )}
      {logo?.asset && logoPosition === 'below' && (
        <img
          src={urlFor(logo).url}
          alt={logo.alt}
          className={clsx(
            styles.logo,
            logoSize && styles[logoSize],
            !ratingTitle && styles.noMargin
          )}
          loading={visibleOnPageLoad ? 'eager' : 'lazy'}
        />
      )}
    </>
  );
};

export default HeroHalfV5HeadlineAndSubtitle;
